import React from "react";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";

export default function AccessibilityStatement() {
  return (
    <LegalLayout
      slug="accessibility"
      eyebrow="Accessibility"
      title="Accessibility Statement"
      lastUpdated="May 26, 2026"
    >
      <p>
        Armstrong K9 Registry is committed to ensuring our
        platform is usable by as many people as possible, including those
        who rely on assistive technology.
      </p>

      <h2>Our standard</h2>
      <p>
        We target conformance with the{" "}
        <a href="https://www.w3.org/TR/WCAG21/" target="_blank" rel="noreferrer">
          Web Content Accessibility Guidelines (WCAG) 2.1, Level AA
        </a>
        . We also follow applicable U.S. accessibility requirements, including
        the Americans with Disabilities Act (ADA) and Section 508 principles
        where they apply to private-sector online services.
      </p>

      <h2>What we do</h2>
      <ul>
        <li>Semantic HTML5 landmarks (<code>&lt;header&gt;</code>, <code>&lt;main&gt;</code>, <code>&lt;nav&gt;</code>, <code>&lt;footer&gt;</code>) so screen readers can navigate the page structure.</li>
        <li>A &ldquo;Skip to main content&rdquo; link that appears when keyboard users press <kbd>Tab</kbd> on any page, jumping past the global navigation directly into the page body (WCAG 2.4.1 Level A).</li>
        <li>Clear, descriptive form labels associated with every input.</li>
        <li>Keyboard-operable controls for all core flows (registration, dog entry, pedigree browsing, billing checkout).</li>
        <li>Focus-visible outlines on interactive elements.</li>
        <li>Colour contrast reviewed against WCAG AA ratios for body text and essential UI.</li>
        <li>Respect for <code>prefers-reduced-motion</code> in our animations and transitions.</li>
        <li><code>alt</code> text on dog photos and logos.</li>
        <li>A toggle on the pedigree page that switches between the visual tree and a screen-reader-friendly nested list ("Text view"), so ancestry can be navigated by list level instead of fighting an SVG canvas.</li>
        <li>Plain-English error messages.</li>
      </ul>

      <h2>Known limitations</h2>
      <p>
        We strive for full AA conformance but acknowledge the following areas
        where we are actively improving:
      </p>
      <ul>
        <li>Printable PDF certificates do not yet include tagged PDF structure — please request an accessible alternate format from our team.</li>
        <li>A small number of third-party payment screens (Stripe Checkout, Google OAuth) follow their providers' own accessibility implementations.</li>
      </ul>

      <h2>Alternate formats &amp; assistance</h2>
      <p>
        If any part of the Registry is inaccessible to you, or if you need a
        pedigree or certificate provided in an alternate format, please
        contact us and we will respond within <strong>5 business days</strong>.
      </p>
      <ul>
        <li>
          Email: <a href="mailto:accessibility@armstrongk9registry.com">accessibility@armstrongk9registry.com</a>
          <ContactFormLink topic="accessibility" subject="Accessibility request" />
        </li>
        <li>Or use our <a href="/contact">Contact Us</a> form.</li>
      </ul>

      <h2>Feedback welcome</h2>
      <p>
        Accessibility is an ongoing commitment. If something on this site
        made your experience harder than it needed to be, please tell us —
        every report helps us fix real-world friction.
      </p>
    </LegalLayout>
  );
}
